import { useState, useMemo } from 'react';
import {
  Table,
  Input,
  Select,
  Space,
  Tag,
  Typography,
  Checkbox,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useServiceRequests, useServiceEngineers } from '@hooks/useService';
import type {
  ServiceRequestItem,
  ServiceRequestFilters,
  ServiceRequestStatus,
  ServiceRequestType,
  ServicePriority,
} from '@/types/service';

const MD_STATUS_OPTIONS: { value: ServiceRequestStatus | ''; label: string }[] = [
  { value: '', label: 'Всі статуси' },
  { value: 'New', label: 'Нова' },
  { value: 'Assigned', label: 'Призначена' },
  { value: 'InProgress', label: 'В роботі' },
  { value: 'WaitingForParts', label: 'Очікує запчастини' },
  { value: 'Completed', label: 'Завершена' },
  { value: 'Closed', label: 'Закрита' },
  { value: 'Cancelled', label: 'Скасована' },
];

const MD_PRIORITY_OPTIONS: { value: ServicePriority | ''; label: string }[] = [
  { value: '', label: 'Всі пріоритети' },
  { value: 'Critical', label: 'Критичний' },
  { value: 'High', label: 'Високий' },
  { value: 'Normal', label: 'Нормальний' },
  { value: 'Low', label: 'Низький' },
];

const MD_STATUS_COLOR_MAP: Record<ServiceRequestStatus, string> = {
  New: 'default',
  Assigned: 'processing',
  InProgress: 'blue',
  WaitingForParts: 'orange',
  Completed: 'green',
  Closed: 'default',
  Cancelled: 'red',
};

const MD_STATUS_LABEL_MAP: Record<ServiceRequestStatus, string> = {
  New: 'Нова',
  Assigned: 'Призначена',
  InProgress: 'В роботі',
  WaitingForParts: 'Очікує запчастини',
  Completed: 'Завершена',
  Closed: 'Закрита',
  Cancelled: 'Скасована',
};

const MD_PRIORITY_COLOR_MAP: Record<ServicePriority, string> = {
  Critical: 'red',
  High: 'orange',
  Normal: 'blue',
  Low: 'default',
};

const MD_PRIORITY_LABEL_MAP: Record<ServicePriority, string> = {
  Critical: 'Критичний',
  High: 'Високий',
  Normal: 'Нормальний',
  Low: 'Низький',
};

const MD_TYPE_LABEL_MAP: Record<ServiceRequestType, string> = {
  Warranty: 'Гарантійний',
  PostWarranty: 'Післягарантійний',
  Maintenance: 'Обслуговування',
  Installation: 'Встановлення',
  Calibration: 'Калібрування',
};

interface ServiceRequestsPageProps {
  onViewRequest: (mdRequestId: string) => void;
}

export default function ServiceRequestsPage({
  onViewRequest,
}: ServiceRequestsPageProps) {
  const [mdSearch, setMdSearch] = useState('');
  const [mdStatusFilter, setMdStatusFilter] = useState<ServiceRequestStatus | ''>('');
  const [mdPriorityFilter, setMdPriorityFilter] = useState<ServicePriority | ''>('');
  const [mdEngineerFilter, setMdEngineerFilter] = useState<string | undefined>();
  const [mdSlaBreached, setMdSlaBreached] = useState(false);
  const [mdPage, setMdPage] = useState(1);
  const [mdPageSize, setMdPageSize] = useState(20);

  const mdFilters: ServiceRequestFilters = useMemo(() => ({
    search: mdSearch || undefined,
    status: mdStatusFilter || undefined,
    priority: mdPriorityFilter || undefined,
    assignedEngineerId: mdEngineerFilter,
    slaBreach: mdSlaBreached || undefined,
    page: mdPage,
    pageSize: mdPageSize,
  }), [mdSearch, mdStatusFilter, mdPriorityFilter, mdEngineerFilter, mdSlaBreached, mdPage, mdPageSize]);

  const { data: mdRequestsData, isLoading: mdIsLoading } = useServiceRequests(mdFilters);
  const { data: mdEngineers, isLoading: mdEngineersLoading } = useServiceEngineers();

  const mdColumns: ColumnsType<ServiceRequestItem> = [
    {
      title: 'Номер',
      dataIndex: 'requestNumber',
      key: 'requestNumber',
      width: 140,
      sorter: (mdA, mdB) => mdA.requestNumber.localeCompare(mdB.requestNumber),
    },
    {
      title: 'Тип',
      dataIndex: 'type',
      key: 'type',
      width: 140,
      render: (mdValue: ServiceRequestType) => MD_TYPE_LABEL_MAP[mdValue] ?? mdValue,
    },
    {
      title: 'Клієнт',
      dataIndex: 'customerName',
      key: 'customerName',
      ellipsis: true,
    },
    {
      title: 'Обладнання',
      dataIndex: 'equipmentSerialNumber',
      key: 'equipmentSerialNumber',
      width: 160,
    },
    {
      title: 'Пріоритет',
      dataIndex: 'priority',
      key: 'priority',
      width: 130,
      render: (mdValue: ServicePriority) => (
        <Tag color={MD_PRIORITY_COLOR_MAP[mdValue]}>
          {MD_PRIORITY_LABEL_MAP[mdValue]}
        </Tag>
      ),
    },
    {
      title: 'SLA дедлайн',
      dataIndex: 'slaDeadline',
      key: 'slaDeadline',
      width: 150,
      render: (mdValue: string | null, mdRecord: ServiceRequestItem) => {
        if (!mdValue) return '—';
        const mdFormatted = dayjs(mdValue).format('DD.MM.YYYY HH:mm');
        if (mdRecord.isSlaBreached) {
          return <Typography.Text type="danger">{mdFormatted}</Typography.Text>;
        }
        return mdFormatted;
      },
    },
    {
      title: 'Інженер',
      dataIndex: 'assignedEngineerName',
      key: 'assignedEngineerName',
      width: 160,
      ellipsis: true,
      // Облікового запису призначеного інженера вже немає — показуємо ідентифікатор
      render: (mdValue: string | null, mdRecord: ServiceRequestItem) => {
        if (mdValue) return mdValue;
        if (!mdRecord.assignedEngineerId) return '—';
        return <Typography.Text type="secondary">{mdRecord.assignedEngineerId}</Typography.Text>;
      },
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 170,
      render: (mdValue: ServiceRequestStatus) => (
        <Tag color={MD_STATUS_COLOR_MAP[mdValue]}>
          {MD_STATUS_LABEL_MAP[mdValue]}
        </Tag>
      ),
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Сервісні заявки
        </Typography.Title>
      </div>

      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Пошук за номером..."
          allowClear
          onSearch={(mdValue) => {
            setMdSearch(mdValue);
            setMdPage(1);
          }}
          style={{ width: 220 }}
        />
        <Select
          options={MD_STATUS_OPTIONS}
          value={mdStatusFilter}
          onChange={(mdValue) => {
            setMdStatusFilter(mdValue);
            setMdPage(1);
          }}
          style={{ width: 200 }}
        />
        <Select
          options={MD_PRIORITY_OPTIONS}
          value={mdPriorityFilter}
          onChange={(mdValue) => {
            setMdPriorityFilter(mdValue);
            setMdPage(1);
          }}
          style={{ width: 180 }}
        />
        <Select
          placeholder="Всі інженери"
          allowClear
          showSearch
          optionFilterProp="label"
          loading={mdEngineersLoading}
          options={(mdEngineers ?? []).map((mdEngineer) => ({ value: mdEngineer.id, label: mdEngineer.fullName }))}
          value={mdEngineerFilter}
          onChange={(mdValue: string | undefined) => {
            setMdEngineerFilter(mdValue);
            setMdPage(1);
          }}
          style={{ width: 220 }}
        />
        <Checkbox
          checked={mdSlaBreached}
          onChange={(mdE) => {
            setMdSlaBreached(mdE.target.checked);
            setMdPage(1);
          }}
        >
          SLA порушено
        </Checkbox>
      </Space>

      <Table<ServiceRequestItem>
        columns={mdColumns}
        dataSource={mdRequestsData?.items}
        rowKey="id"
        loading={mdIsLoading}
        onRow={(mdRecord) => ({
          onClick: () => onViewRequest(mdRecord.id),
          style: { cursor: 'pointer' },
        })}
        pagination={{
          current: mdPage,
          pageSize: mdPageSize,
          total: mdRequestsData?.totalCount,
          showSizeChanger: true,
          showTotal: (mdTotal) => `Всього: ${mdTotal}`,
          onChange: (mdNewPage, mdNewPageSize) => {
            setMdPage(mdNewPage);
            setMdPageSize(mdNewPageSize);
          },
        }}
        size="middle"
      />
    </>
  );
}
